import { Box, styled } from '@mui/material';
import { IFilters, ITaskSearchParam } from 'wisestep-api';
import { formatDateInDayMonthYear, WsColors, WsSelectObj } from 'wisestep-base';

import { ListingSearchPropsForNotesListing } from '../../../features/clientDetails/components/notes/helper/notesHelper';

export const notesDefaultFilterValues = {
  search: '',
  fromDate: '',
  toDate: '',
  createdBy: [],
  clientIds: [],
  contactIds: [],
  clients: [],
  contacts: [],
  tags: [],
  reminderStatus: '',
  type: '',
  searchAcross: [],
  noteType: '',
  clientNames: [],
  query: '',
};

export const getFilterValue = (
  fieldName: string,
  methods,
  defaultSearchFiltersValues?: ListingSearchPropsForNotesListing
) => {
  if (defaultSearchFiltersValues?.type === 'client' && fieldName === 'clients') {
    return (defaultSearchFiltersValues?.clientIds?.length ?? 0) > 0 ? 'Selected 1' : 'All';
  }

  if (defaultSearchFiltersValues?.type === 'client' && fieldName === 'contacts') {
    return (defaultSearchFiltersValues?.contactIds?.length ?? 0) > 0 ? 'Selected 1' : 'None';
  }

  if (defaultSearchFiltersValues?.type === 'client_contact' && fieldName === 'contacts') {
    return (defaultSearchFiltersValues?.contactIds?.length ?? 0) > 0 ? 'Selected 1' : 'All';
  }

  if (defaultSearchFiltersValues?.type === 'client_contact' && fieldName === 'clients') {
    return (defaultSearchFiltersValues?.clientIds?.length ?? 0) > 0 ? 'Selected 1' : 'None';
  }

  const fieldValue = methods.watch(fieldName);

  if (Array.isArray(fieldValue)) {
    return fieldValue.length > 0 ? `Selected ${fieldValue.length}` : `All`;
  }
  return fieldValue ? `Selected 1` : `All`;
};

export const stripHtmlTags = (html) => {
  return html ? html.replace(/<[^>]+>/g, '') : '';
};

export const NotesBoxWithRadius = styled(Box)<{ status: string }>(({ status }) => ({
  border: '1px solid',
  borderColor: status === 'OPEN' ? WsColors.Yellow100 : WsColors.Green500,
  backgroundColor: status === 'OPEN' ? WsColors.Yellow200 : WsColors.Green500,
  borderRadius: 4,
  paddingLeft: 8,
  paddingRight: 8,
  height: 30,
  alignItems: 'center',
}));

export const TaskDefaultQuery: ITaskSearchParam = {
  searchType: '',
  status: '',
  dueFromDateTime: null,
  dueTillDateTime: null,
  createdFromDateTime: null,
  createdTillDateTime: null,
  updatedFromDateTime: null,
  updatedTillDateTime: null,
  createdBy: [],
  team: [],
  sort: 'due_date',
  sortDirection: 'DESC',
  size: 50,
  page: 0,
  keywords: '',
};
export const leadTypes = ['All Leads', 'My Leads'];

export const noteTypes = ['All Notes', 'My Notes', 'Unmarked Notes'];
export const noteTypesNew: WsSelectObj[] = [
  { label: 'All Notes', value: 'all_notes' },
  { label: 'My Notes', value: 'my_notes' },
  { label: 'Unmarked Notes', value: 'unmarked_notes' },
];

export const getNoteTypeLabel = (noteType?: string): string => {
  const noteTypeLabels = {
    my_notes: 'My Notes',
    unmarked_notes: 'Unmarked Notes',
    all_notes: 'All Notes',
  };

  return noteTypeLabels[noteType ?? 'all_notes'] || 'All Notes';
};

export const allowedKeys: string[] = [
  'fromDate',
  'toDate',
  'createdBy',
  'clients',
  'contacts',
  'tags',
  'reminderStatus',
];

export const statusArray = [
  {
    label: 'All',
    value: 'ALL',
  },
  {
    label: 'Upcoming',
    value: 'UPCOMING',
  },
  {
    label: 'Overdue',
    value: 'OVERDUE',
  },
  {
    label: 'Closed',
    value: 'CLOSED',
  },
];

export const hasValue = (val: ListingSearchPropsForNotesListing): boolean => {
  if (Array.isArray(val)) return val.length > 0;
  if (val && typeof val === 'object') return Object.values(val).some(hasValue);
  return Boolean(val);
};

export const hasFilterValue = (filterValue?: IFilters): boolean => {
  type AllowedKey = keyof typeof notesDefaultFilterValues;
  const filtered = allowedKeys.reduce((acc, key) => {
    acc[key] = filterValue?.[key];
    return acc;
  }, {} as Pick<typeof notesDefaultFilterValues, AllowedKey>);

  const advFilterCheck = hasValue(filtered);
  return advFilterCheck || (!!filterValue?.toDate && filterValue.toDate.toString()?.trim() !== '');
};

export interface BadgeActionProps {
  getValues: (field: string) => string | undefined;
  onClearFilter?: (filterType: string, label?: string, index?: number) => void;
  filterValue?: IFilters;
}

export const getBadges = ({ getValues, onClearFilter, filterValue }: BadgeActionProps) => {
  const badges: {
    key: string | number;
    label: string;
    onClick: () => void;
  }[] = [];

  if (filterValue?.fromDate && filterValue?.toDate) {
    badges.push({
      key: 'date-range',
      label: `${formatDateInDayMonthYear(
        new Date(getValues('fromDate') ?? '')
      )} to ${formatDateInDayMonthYear(new Date(getValues('toDate') ?? ''))}`,
      onClick: () => onClearFilter?.('dateRange'),
    });
  }

  filterValue?.createdBy?.forEach((label, index) =>
    badges.push({
      key: `createdBy-${index}`,
      label: `Created By: ${label}`,
      onClick: () => onClearFilter?.('createdBy', label, index),
    })
  );

  filterValue?.clients?.forEach((client, index) =>
    badges.push({
      key: `client-${index}`,
      label: `Client: ${client.name}`,
      onClick: () => onClearFilter?.('client', 'client', index),
    })
  );

  filterValue?.contacts?.forEach((contact, index) =>
    badges.push({
      key: `contact-${index}`,
      label: `Contact: ${contact.firstName}`,
      onClick: () => onClearFilter?.('contact', 'contact', index),
    })
  );

  filterValue?.tags?.forEach((tag, index) =>
    badges.push({
      key: `tag-${index}`,
      label: `Tag: ${tag}`,
      onClick: () => onClearFilter?.('tag', tag, index),
    })
  );

  if (filterValue?.reminderStatus) {
    badges.push({
      key: 'reminder',
      label: `Reminder: ${getValues('reminderStatus')}`,
      onClick: () => onClearFilter?.('reminder'),
    });
  }

  return badges;
};
